// re.rejouis.fr — connexion client via Shopify Customer Account API (OAuth 2.0
// Authorization Code + PKCE, client PUBLIC). Le « Connecte-toi avec ton compte
// rejouis.fr ».
//
// Flux :
//  1. login()        → génère PKCE, redirige vers account.rejouis.fr (Shopify)
//  2. /callback      → handleCallback() envoie le code à la fonction serveur
//                      `customer-auth` qui échange le token et lit le profil
//  3. session client = profil { id, email, prenom } stocké localement
//     (le token d'accès Shopify reste côté serveur, jamais dans le navigateur)
//
// Endpoints découverts via .well-known (shop 78601978201).

const SHOP_ID = import.meta.env.VITE_SHOPIFY_SHOP_ID;
const CLIENT_ID = import.meta.env.VITE_SHOPIFY_CUSTOMER_CLIENT_ID;
const SCOPES = 'openid email customer-account-api:full';
const SESSION_KEY = 'reprise_client';
const TOKEN_KEY = 'reprise_session'; // token signé (cf. netlify/functions/_session.js)

/** Token de session signé à envoyer en Authorization: Bearer sur demande/lot. */
export function sessionToken() {
  return localStorage.getItem(TOKEN_KEY) || '';
}

export function authConfigure() {
  return Boolean(SHOP_ID && CLIENT_ID);
}

function redirectUri() {
  return window.location.origin + '/callback';
}

// base64url d'un ArrayBuffer / Uint8Array
function b64url(buf) {
  const bytes = new Uint8Array(buf);
  let str = '';
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function pkce() {
  const verifier = b64url(crypto.getRandomValues(new Uint8Array(32)));
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return { verifier, challenge: b64url(digest) };
}

/** Redirige vers la page de connexion Shopify (compte rejouis.fr).
 *  `silencieux` : prompt=none → Shopify reconnecte sans rien afficher si une session
 *  client est déjà ouverte (client connecté sur rejouis.fr), sinon revient avec
 *  error=login_required, qu'on ignore (cf. handleCallback). */
export async function login({ silencieux = false } = {}) {
  if (!authConfigure()) throw new Error('Connexion non configurée (Client ID manquant).');
  const { verifier, challenge } = await pkce();
  const state = b64url(crypto.getRandomValues(new Uint8Array(16)));
  sessionStorage.setItem('pkce_verifier', verifier);
  sessionStorage.setItem('oauth_state', state);
  if (silencieux) sessionStorage.setItem('oauth_silencieux', '1');
  else sessionStorage.removeItem('oauth_silencieux');

  const url = new URL(`https://shopify.com/authentication/${SHOP_ID}/oauth/authorize`);
  url.searchParams.set('client_id', CLIENT_ID);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('redirect_uri', redirectUri());
  url.searchParams.set('scope', SCOPES);
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  if (silencieux) url.searchParams.set('prompt', 'none');
  window.location.href = url.toString();
}

/** Au retour sur /callback : échange le code (via la fonction serveur) → profil. */
export async function handleCallback() {
  const p = new URLSearchParams(window.location.search);
  const code = p.get('code');
  const state = p.get('state');
  const err = p.get('error');
  const silencieux = sessionStorage.getItem('oauth_silencieux') === '1';
  sessionStorage.removeItem('oauth_silencieux');
  // Connexion silencieuse sans session Shopify ouverte : pas une erreur, on reste anonyme.
  if (err && silencieux) return null;
  if (err) throw new Error(`Connexion refusée (${err}).`);
  if (!code) return null;
  if (state !== sessionStorage.getItem('oauth_state')) throw new Error('État OAuth invalide.');

  const verifier = sessionStorage.getItem('pkce_verifier');
  const res = await fetch('/api/customer-auth', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, verifier, redirectUri: redirectUri() }),
  });
  sessionStorage.removeItem('pkce_verifier');
  sessionStorage.removeItem('oauth_state');
  if (!res.ok) {
    let m = `Erreur ${res.status}`;
    try { m = (await res.json()).error || m; } catch (e) { /* ignore */ }
    throw new Error(m);
  }
  const { customer, session } = await res.json();
  localStorage.setItem(SESSION_KEY, JSON.stringify(customer));
  if (session) localStorage.setItem(TOKEN_KEY, session);
  return customer;
}

/** Le token de session est présent et non expiré (lecture de exp sans vérifier la signature :
 *  le serveur fait foi — on évite juste d'afficher « connecté » à tort). */
export function sessionValide() {
  try {
    const { payload } = JSON.parse(atob(sessionToken().replace(/-/g, '+').replace(/_/g, '/')));
    return Date.now() < JSON.parse(payload).exp;
  } catch (e) { return false; }
}

/** Profil client connecté, ou null. Le profil survit au rechargement mais le token signé
 *  expire (12 h, cf. _session.js) : profil sans session valide = déconnecté, sinon l'écran
 *  affiche « connecté » et chaque appel serveur répond « Connexion requise ». */
export function clientConnecte() {
  let profil = null;
  try { profil = JSON.parse(localStorage.getItem(SESSION_KEY)); } catch (e) { profil = null; }
  if (profil && !sessionValide()) { logout(); return null; }
  return profil;
}

export function logout() {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(TOKEN_KEY);
}
